import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import TwoFactorSetup from "@/components/TwoFactorSetup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authStatus, authSetup, totpConfirm, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight } from "lucide-react";

const Setup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    authStatus().then((s) => {
      if (!s.needs_setup) navigate("/login", { replace: true });
      else if (s.admin_email) setAdminEmail(s.admin_email);
    }).catch(() => {});
  }, [navigate]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const data = await authSetup({ password, confirm_password: confirm });
      setSetupData(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmTotp = async (code) => {
    setError("");
    setLoading(true);
    try {
      const res = await totpConfirm({ setup_token: setupData.setup_token, code });
      login(res.token, res.user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (setupData) {
    return (
      <AuthShell title="Secure your account" subtitle="Set up two-factor authentication to finish.">
        <TwoFactorSetup qrPng={setupData.qr_png} otpauthUri={setupData.otpauth_uri} onSubmit={confirmTotp} loading={loading} error={error} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="First-time setup" subtitle="Create the administrator password for this workspace.">
      <form onSubmit={submitPassword} className="space-y-5">
        <div>
          <Label className="label-caps">Admin Email</Label>
          <Input value={adminEmail} disabled className="mt-1.5 bg-slate-50" data-testid="setup-email" />
        </div>
        <div>
          <Label className="label-caps">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoFocus className="mt-1.5" data-testid="setup-password" />
        </div>
        <div>
          <Label className="label-caps">Confirm Password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1.5" data-testid="setup-confirm" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="setup-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="setup-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Continue to 2FA
        </Button>
      </form>
    </AuthShell>
  );
};

export default Setup;
